// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import { Text } from '../Text.js';
import Latex from '../Latex.js';
import { List } from '../List.js';
import Image from '../Image.js';
import BoxAndWhiskerPlot from '../BoxAndWhiskerPlot.js';
import { CartesianPlane } from '../CartesianPlane.js';
import { NumberLine } from '../NumberLine.js';
import { StemAndLeafPlot } from '../StemAndLeafPlot.js';
import TabularData from '../TabularData.js';
import { TwoColumnProof } from '../TwoColumnProof.js';
import { AlgebraicWorking } from '../AlgebraicWorking.js';
import { SolidifyAnnotatedFigure } from './SolidifyAnnotatedFigure.js';
import { SLDExplainedEquation } from './SLDExplainedEquation.js';
import { SLDTermDefinition } from './SLDTermDefinition.js';
import { SolidifyFigureGrid } from './SolidifyFigureGrid.js';
import { Heading } from '../Heading.js';
import { productColor } from '../designSystem.js';
import { Language, English, Spanish } from '../languages.js';
import DraftImage from '../DraftImage.js';
import { LatexTextAtom } from '../LatexTextAtom.js';

export function SLDConceptSummary(props: {|
  children: ChildrenArray<
    | Element<typeof Text>
    | Element<typeof Latex>
    | Element<typeof LatexTextAtom>
    | Element<typeof List>
    | Element<typeof Image>
    | Element<typeof BoxAndWhiskerPlot>
    | Element<typeof CartesianPlane>
    | Element<typeof NumberLine>
    | Element<typeof StemAndLeafPlot>
    | Element<typeof TabularData>
    | Element<typeof SolidifyAnnotatedFigure>
    | Element<typeof SLDExplainedEquation>
    | Element<typeof SLDTermDefinition>
    | Element<typeof SolidifyFigureGrid>
    | Element<typeof TwoColumnProof>
    | Element<typeof AlgebraicWorking>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(props, SLDConceptSummary);
  if (failedValidation) return null;
  return (
    <div style={{ color: productColor.shuttleGrey, marginLeft: 30 }}>
      <Heading>
        <Language>
          <English>Concept summary</English>
          <Spanish>Resumen del concepto</Spanish>
        </Language>
      </Heading>
      <div style={{ height: 8 }} />
      <div>{props.children}</div>
      <div style={{ height: 8 * 6 }} />
    </div>
  );
}
SLDConceptSummary.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      // Primitives
      t.elementType(Text),
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
      t.elementType(List),
      t.elementType(Image),
      t.elementType(DraftImage),
      t.elementType(BoxAndWhiskerPlot),
      t.elementType(CartesianPlane),
      t.elementType(NumberLine),
      t.elementType(StemAndLeafPlot),
      t.elementType(TabularData),
      t.elementType(TwoColumnProof),
      t.elementType(AlgebraicWorking),
      // components designed for SLD
      t.elementType(SLDExplainedEquation),
      t.elementType(SLDTermDefinition),
      // generic components designed for Solidify
      t.elementType(SolidifyAnnotatedFigure),
      t.elementType(SolidifyFigureGrid),
    ),
  ),
};
